import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingCart, Sparkles, Timer, X, Zap } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "@/context/CartContext";

function useCountdown() {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const now = new Date();
    const target = new Date(now);
    target.setUTCDate(now.getUTCDate() + ((7 - now.getUTCDay()) % 7 || 7));
    target.setUTCHours(23, 59, 59, 999);
    const tick = () => {
      const ms = target.getTime() - Date.now();
      if (ms <= 0) return setLeft("ENDED");
      const s = Math.floor(ms / 1000);
      const dd = Math.floor(s / 86400);
      const hh = Math.floor((s % 86400) / 3600);
      const mm = Math.floor((s % 3600) / 60);
      const ss = s % 60;
      setLeft(`${dd}D ${String(hh).padStart(2, "0")}H ${String(mm).padStart(2, "0")}M ${String(ss).padStart(2, "0")}S`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return left;
}

export default function Navbar() {
  const { count, setIsOpen, setAssistantOpen } = useCart();
  const { pathname } = useLocation();
  const countdown = useCountdown();
  const [bannerOpen, setBannerOpen] = useState(
    () => localStorage.getItem("voltaic_banner_closed") !== "1"
  );

  const closeBanner = () => {
    localStorage.setItem("voltaic_banner_closed", "1");
    setBannerOpen(false);
  };

  const linkCls = (active) =>
    `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-200 ${
      active ? "text-voltaic-cyan" : "text-voltaic-muted hover:text-voltaic-ink"
    }`;

  return (
    <header data-testid="navbar" className="fixed inset-x-0 top-0 z-40">
      <AnimatePresence initial={false}>
        {bannerOpen && (
          <motion.div
            key="sale-banner"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="sale-banner"
            className="relative overflow-hidden bg-voltaic-cyan text-voltaic-bg"
          >
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-0.5 px-10 py-2.5 text-center font-mono text-[10px] font-bold uppercase tracking-[0.22em] sm:text-[11px]">
              <Zap size={12} className="shrink-0" />
              Flash drop — extra 20% off everything with code VOLTAIC20
              <span data-testid="sale-banner-countdown" className="flex items-center gap-1.5 rounded-full bg-voltaic-bg/15 px-3 py-0.5 tabular-nums">
                <Timer size={11} /> Ends in {countdown}
              </span>
              <button
                data-testid="sale-banner-close"
                onClick={closeBanner}
                aria-label="Dismiss banner"
                className="absolute right-3 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full transition-colors hover:bg-voltaic-bg/10"
              >
                <X size={13} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-10">
          <Logo />
          <nav className="hidden items-center gap-8 md:flex">
            <Link to="/" data-testid="nav-home-link" className={linkCls(pathname === "/")}>Home</Link>
            <Link to="/shop" data-testid="nav-shop-link" className={linkCls(pathname === "/shop")}>Shop</Link>
            <Link to="/orders" data-testid="nav-orders-link" className={linkCls(pathname === "/orders")}>Orders</Link>
            <Link to="/guides" data-testid="nav-guides-link" className={linkCls(pathname.startsWith("/guides"))}>Guides</Link>
            <button
              data-testid="nav-ai-button"
              onClick={() => setAssistantOpen(true)}
              className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-voltaic-muted transition-colors duration-200 hover:text-voltaic-cyan"
            >
              <Sparkles size={13} /> Ask VOLT AI
            </button>
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden items-center gap-2 rounded-full border border-voltaic-line px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-voltaic-muted sm:flex">
              <span className="h-1.5 w-1.5 animate-pulseDot rounded-full bg-voltaic-green" />
              DROP 07 LIVE
            </span>
            <button
              data-testid="navbar-cart-button"
              onClick={() => setIsOpen(true)}
              aria-label="Open cart"
              className="relative grid h-10 w-10 place-items-center rounded-full border border-voltaic-line bg-voltaic-surface text-voltaic-ink transition-colors duration-200 hover:border-voltaic-cyan"
            >
              <ShoppingCart size={16} />
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    key="badge"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    data-testid="cart-count-badge"
                    className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-voltaic-cyan px-1 text-[10px] font-bold text-voltaic-bg"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
